import type { Private } from '@seek/indie-api-types';
import {
  Alert,
  ContentBlock,
  Divider,
  Heading,
  Stack,
  Strong,
  Text,
  TextLink,
} from 'braid-design-system';
import { useState } from 'react';

import { useApi } from '#src/api/index.ts';

import { BasePage } from '#src/components/BasePage/BasePage.tsx';
import type { Breadcrumb } from '#src/components/Breadcrumbs/Breadcrumbs.tsx';
import { Request } from './components/Request/Request.tsx';
import { TestWebhookForm } from './components/TestWebhookForm/TestWebhookForm.tsx';
import type { FormData } from './components/TestWebhookForm/form.ts';

export const WebhooksPlaygroundPage = ({
  breadcrumbs,
}: {
  breadcrumbs: Breadcrumb[];
}) => {
  const [loading, setLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | undefined>();
  const [request, setRequest] = useState<Private.TestCallInput | undefined>();
  const [result, setResult] = useState<Private.TestCallOutput | undefined>();

  const api = useApi();

  const onSubmit = async (details: FormData) => {
    try {
      const input = {
        url: details.url,
        input: {
          eventTypeCode: details.eventTypeCode,
          variation: details.eventVariation,
        },
      } as Private.TestCallInput;
      setRequest(input);
      setLoading(true);
      const response = await api.testEvents.sendTestEvent(input);
      setResult(response);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    }
    setLoading(false);
  };

  return (
    <BasePage title="Webhook playground" breadcrumbs={breadcrumbs}>
      <ContentBlock width="medium" align="left">
        <Stack space="large">
          <Stack space="medium">
            <Text>
              Use this form to configure and send playground webhook events to a
              URL of your choice. To instead receive production webhook events
              from SEEK, you will need to configure a subscription on the{' '}
              <TextLink href="/manage/webhooks">webhook subscriptions</TextLink>{' '}
              page.
            </Text>

            <Text>
              This tool will send webhook events with references to{' '}
              <TextLink href="/graphql/playground/mock-objects">
                mock objects
              </TextLink>{' '}
              in the playground environment. To query these references (e.g. a
              candidate profile) you must use{' '}
              <Strong>
                <TextLink href="/graphql/playground#authentication">
                  playground credentials{' '}
                </TextLink>
              </Strong>
              when authorizing the request.
            </Text>
          </Stack>

          <Divider />

          <Heading level="4">Send a playground webhook event</Heading>

          <TestWebhookForm onSubmit={onSubmit} loading={loading} />

          {error ? (
            <Alert tone="critical">
              <Stack space="medium">
                <Text>
                  An error occurred while trying to process your request.
                </Text>
                <Text>{error}</Text>
              </Stack>
            </Alert>
          ) : null}

          {request && (
            <>
              <Divider />

              <Heading level="4">Test event details</Heading>
              <Request response={result} request={request} loading={loading} />
            </>
          )}
        </Stack>
      </ContentBlock>
    </BasePage>
  );
};
